<template>
    <div class="paginated-item-list">
        <div class="paginated-item-list__title">{{ title }}</div>
        <div class="paginated-item-list__description">{{ description }} <span>{{ spanDescription }}</span></div>
        <KLoader v-if="loading" />
        <div v-if="!loading && items.length > 0" class="paginated-item-list__item-main-wrapper">
            <div v-for="item in items" :key="item.id" class="paginated-item-list__item-wrapper" >
                <div v-if="item.video_url == null && item.image == null" class="paginated-item-list__img-wrapper paginated-item-list__img-wrapper--plug kasanowa-hover" @click="navigateTo(`/${btnRedirect}/${item.id}`)">
                    <KIcon icon="image" width="200" height="200" />
                </div>
                <div v-if="item.video_url == null && item.image != null" class="paginated-item-list__img-wrapper kasanowa-hover" @click="navigateTo(`/${btnRedirect}/${item.id}`)">
                    <img :src="item.image" alt="kasanowa" />
                </div>
                <div v-if="item.video_url != null" class="paginated-item-list__img-wrapper paginated-item-list__img-wrapper--iframe kasanowa-hover" @click="navigateTo(`/${btnRedirect}/${item.id}`)">
                    <iframe
                        width="100%"
                        :height="handleIframeHeight()"
                        :src="`${item.video_url}&autoplay=1&mute=1`"
                        title="YouTube video player"
                        frameborder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerpolicy="strict-origin-when-cross-origin"
                        allowfullscreen
                    />
                </div>
                <div class="paginated-item-list__content-wrapper">
                    <div class="paginated-item-list__item-title kasanowa-hover" @click="navigateTo(`/${btnRedirect}/${item.id}`)">{{ item.name }}</div>
                    <div class="paginated-item-list__item-lead">{{ item.lead }}</div>
                    <div class="paginated-item-list__btn-wrapper">
                        <KButton
                            :id="`article-${item.id}`"
                            :label="$t('articles.more')"
                            red
                            uppercase
                            icon="arrowRight"
                            @click="navigateTo(`/${btnRedirect}/${item.id}`)"
                        />
                    </div>
                </div>
            </div>

            <div class="paginated-item-list__pagination-wrapper">
                <v-pagination v-model="currentPage" :length="lastPage" rounded="circle" />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { defineProps, onUnmounted, onMounted, ref, watch } from 'vue';
import { navigateTo } from '@/hooks/NavigateHooks';
import KLoader from '@/components/atoms/KLoader.vue';
import KButton from '@/components/atoms/KButton.vue';
import KIcon from '@/components/atoms/KIcon.vue';

interface IOwnProps {
    title: string
    description: string
    spanDescription: string
    lastPage: number
    loading: boolean
    items: any[]
    btnRedirect: string
    changePage: (page: number) => void
}
interface IProps extends IOwnProps {
}

const props = defineProps<IProps>();

const handleIframeHeight = (): string => {
    const currentWindowWidth = window.innerWidth;
    if (currentWindowWidth < 768) {
        return '240px';
    }
    if (currentWindowWidth >= 768 && currentWindowWidth < 992) {
        return '350px';
    }
    if (currentWindowWidth >= 992 && currentWindowWidth < 1200) {
        return '250px';
    }
    if (currentWindowWidth >= 1200) {
        return '280px';
    }
    return '250px';
}
onMounted(() => {
    addEventListener('resize', handleIframeHeight);
});
onUnmounted(() => {
   removeEventListener('resize', handleIframeHeight);
});
const currentPage = ref<number>(1);
watch(() => currentPage.value, (newValue, oldValue) => {
    if (newValue !== oldValue) {
        props.changePage(newValue);
    }
}, {
    deep: true,
});
</script>

<style scoped lang="scss">
.paginated-item-list {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;

    &__title {
        background-color: $red-400;
        color: $white;
        font-weight: 900;
        font-size: 22px;
        text-transform: uppercase;
        text-align: center;
        padding: 10px 7px;
        letter-spacing: 0;

        @include media-breakpoint-up(lg) {
            width: max-content;
            font-size: 52px;
            padding: 10px 35px;
        }
    }

    &__description {
        margin-top: 20px;
        color: $white;

        span {
            color: $red-400;
            font-weight: 700;
        }
    }

    &__item-main-wrapper {
        display: flex;
        flex-direction: column;
        gap: 41px;
        margin-top: 120px;

        @include media-breakpoint-up(lg) {
            gap: 131px;
        }
    }

    &__item-wrapper {
        display: flex;
        flex-direction: column;

        @include media-breakpoint-up(lg) {
            flex-direction: row;
            gap: 20px;
        }

        @include media-breakpoint-up(xl) {
            flex-direction: row;
            gap: 120px;
        }
    }

    &__img-wrapper {
        width: 300px;
        min-width: 300px;
        height: 240px;
        min-height: 240px;
        background-color: $primary-800;
        border-radius: 8px;

        @include media-breakpoint-up(sm) {
            width: 400px;
            min-width: 400px;
        }

        @include media-breakpoint-up(md) {
            width: 700px;
            min-width: 700px;
            height: 350px;
            min-height: 350px;
        }

        @include media-breakpoint-up(lg) {
            width: 450px;
            min-width: 450px;
            height: 250px;
            min-height: 250px;
        }

        @include media-breakpoint-up(xl) {
            height: 280px;
            min-height: 280px;
            width: 550px;
            min-width: 550px;
        }

        &--plug {
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;

            &::before {
                position: absolute;
                content: '';
                width: 80%;
                height: 7px;
                background-color: $white;
                transform: rotateZ(45deg);

                @include media-breakpoint-up(sm) {
                    width: 60%;
                }

                @include media-breakpoint-up(md) {
                    width: 40%;
                }

                @include media-breakpoint-up(lg) {
                    width: 50%;
                }

                @include media-breakpoint-up(xl) {
                    width: 40%;
                }
            }

            &::after {
                position: absolute;
                content: '';
                width: 80%;
                height: 7px;
                top: 52%;
                background-color: $primary-800;
                transform: rotateZ(45deg);

                @include media-breakpoint-up(sm) {
                    width: 60%;
                }

                @include media-breakpoint-up(md) {
                    width: 40%;
                }

                @include media-breakpoint-up(lg) {
                    width: 50%;
                }

                @include media-breakpoint-up(xl) {
                    width: 40%;
                }
            }

            svg {
                fill: $white;
            }
        }

        img {
            object-fit: cover;
            width: 100%;
            min-width: 100%;
            height: 100%;
            min-height: 100%;
            border-radius: 10px;
        }
    }

    &__content-wrapper {
        display: flex;
        flex-direction: column;
        width: 300px;

        @include media-breakpoint-up(sm) {
            width: 400px;
        }

        @include media-breakpoint-up(md) {
            width: 700px;
        }

        @include media-breakpoint-up(lg) {
            width: calc(100% - 450px - 20px);
        }

        @include media-breakpoint-up(xl) {
            width: calc(100% - 450px - 120px);
        }

        @include media-breakpoint-up(xxl) {
            width: calc(100% - 500px - 120px);
        }
    }

    &__item-title {
        color: $white;
        font-weight: 700;
        text-transform: uppercase;
        margin: 10px 0 6px;
        font-size: 18px;

        @include media-breakpoint-up(lg) {
            font-size: 28px;
            margin: 0 0 6px;
            width: 450px;
            height: 70px;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 1.4;
        }

        @include media-breakpoint-up(xxl) {
            width: 500px;
        }
    }

    &__item-lead {
        color: $white;

        @include media-breakpoint-up(lg) {
            width: 450px;
            height: 121px;
            display: -webkit-box;
            -webkit-line-clamp: 6;
            line-clamp: 6;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            line-height: 1.4;
        }

        @include media-breakpoint-up(xl) {
            height: 153px;
            -webkit-line-clamp: 8;
            line-clamp: 8;
        }

        @include media-breakpoint-up(xxl) {
            width: 500px;
        }
    }

    &__btn-wrapper {
        margin-top: 10px;
    }

    &__pagination-wrapper {
        width: 100%;
        max-width: 290px;
        margin: 0 auto;
        color: $white;

        @include media-breakpoint-up(sm) {
            max-width: 320px;
        }

        @include media-breakpoint-up(md) {
            max-width: 70%;
        }

        @include media-breakpoint-up(lg) {
            max-width: 60%;
        }

        @include media-breakpoint-up(xl) {
            max-width: 530px;
        }

        &:deep(.v-pagination__next) {
            border: 1px solid $red-400;
            color: $red-400;
            border-radius: 50%;
        }

        &:deep(.v-pagination__prev) {
            border: 1px solid $red-400;
            color: $red-400;
            border-radius: 50%;
        }
    }
}
</style>
